import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TacticalSpecialist, PerformanceKicker } from '@/types/project';
import { formatCurrency } from '../utils';
import { KICKER_TIERS } from '../constants';
import { TierBadge } from '../shared/TierBadge';

interface KickerCalculation {
  id: string;
  name: string;
  bonus: number;
  specialistPayout: number;
  perPersonPayout: number;
  eligibleCount: number;
  ownerPayout: number;
  tierInfo: typeof KICKER_TIERS[0] | undefined;
}

interface OverviewTabProps {
  revenue: number;
  baseProfit: number;
  profitMargin: number;
  operationsCost: number;
  totalOperationsCompensation: number;
  operationsOverheadPercentage: number;
  baseOperationsCost: number;
  totalTacticalCosts: number;
  tacticalBudgetRemaining: number;
  totalKickerBonus: number;
  totalKickerOwnerRevenue: number;
  totalKickerSpecialistPayouts: number;
  totalRevenue: number;
  totalProfit: number;
  totalProjectCosts: number;
  tacticalSpecialists: TacticalSpecialist[];
  operationsOwners: string[];
  performanceKickers: PerformanceKicker[];
  kickerCalculations: KickerCalculation[];
}

export function OverviewTab({
  revenue,
  baseProfit,
  profitMargin,
  totalOperationsCompensation,
  operationsOverheadPercentage,
  baseOperationsCost,
  totalTacticalCosts,
  tacticalBudgetRemaining,
  totalKickerBonus,
  totalKickerOwnerRevenue,
  totalKickerSpecialistPayouts,
  totalRevenue,
  totalProfit,
  totalProjectCosts,
  tacticalSpecialists,
  operationsOwners,
  performanceKickers,
  kickerCalculations,
}: OverviewTabProps) {
  // Build bonus eligibility data
  const bonusEligibilityData = buildBonusEligibilityData(
    tacticalSpecialists,
    operationsOwners,
    performanceKickers,
    kickerCalculations
  );

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 lg:gap-6">
        <Card>
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs md:text-sm font-medium">Monthly Retainer</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="text-lg md:text-xl lg:text-2xl font-bold font-serif">{formatCurrency(revenue)}</div>
            <p className="text-xs text-muted-foreground hidden md:block">
              Gross monthly revenue
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs md:text-sm font-medium">Owner Distribution</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="text-lg md:text-xl lg:text-2xl font-bold font-serif text-positive">
              {formatCurrency(baseProfit)}
            </div>
            <p className="text-xs text-muted-foreground hidden md:block">
              {profitMargin}% of retainer
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs md:text-sm font-medium">Operations Budget</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="text-lg md:text-xl lg:text-2xl font-bold font-serif">{formatCurrency(revenue - baseProfit)}</div>
            <p className="text-xs text-muted-foreground hidden md:block">
              Remaining for costs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs md:text-sm font-medium">Performance Bonuses</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="text-lg md:text-xl lg:text-2xl font-bold font-serif text-info">
              {formatCurrency(totalKickerBonus)}
            </div>
            <p className="text-xs text-muted-foreground hidden md:block">
              Performance revenue
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Flow - Collapsible on mobile */}
      <Accordion type="single" collapsible defaultValue="revenue-flow" className="w-full">
        <AccordionItem value="revenue-flow" className="border rounded-lg">
          <AccordionTrigger className="px-4 sm:px-6 hover:no-underline">
            <span className="text-sm font-medium">Revenue Flow Breakdown</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 sm:px-6 pb-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">1. Monthly Retainer (Gross Revenue)</span>
                <span className="font-bold font-serif">{formatCurrency(revenue)}</span>
              </div>
              <div className="flex items-center justify-between text-sm pl-4 text-positive">
                <span>↳ Owner Distribution ({profitMargin}% margin taken first)</span>
                <span className="font-bold font-serif">- {formatCurrency(baseProfit)}</span>
              </div>
              <div className="flex items-center justify-between text-sm font-medium border-t pt-2">
                <span>2. Remaining Operations Budget</span>
                <span className="font-bold font-serif">{formatCurrency(revenue - baseProfit)}</span>
              </div>
              <div className="flex items-center justify-between text-sm pl-4">
                <span>↳ Operations Compensation</span>
                <span>- {formatCurrency(totalOperationsCompensation)}</span>
              </div>
              <div className="flex items-center justify-between text-sm pl-4">
                <span>↳ Operations Overhead ({operationsOverheadPercentage}%)</span>
                <span>- {formatCurrency(baseOperationsCost)}</span>
              </div>
              <div className="flex items-center justify-between text-sm pl-4">
                <span>↳ Team Members</span>
                <span>- {formatCurrency(totalTacticalCosts)}</span>
              </div>
              <div className={`flex items-center justify-between text-sm font-bold border-t pt-2 ${tacticalBudgetRemaining >= 0 ? 'text-positive' : 'text-negative'}`}>
                <span>3. Remaining Budget</span>
                <span className="font-serif">{formatCurrency(tacticalBudgetRemaining)}</span>
              </div>
              {totalKickerBonus > 0 && (
                <>
                  <div className="flex items-center justify-between text-sm font-medium border-t pt-2 text-info">
                    <span>4. Performance Bonuses</span>
                    <span className="font-bold font-serif">+ {formatCurrency(totalKickerBonus)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm pl-4 text-positive">
                    <span>↳ Additional Owner Distribution</span>
                    <span className="font-bold font-serif">+ {formatCurrency(totalKickerOwnerRevenue)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm pl-4 text-info">
                    <span>↳ Specialist Bonuses</span>
                    <span className="font-bold font-serif">+ {formatCurrency(totalKickerSpecialistPayouts)}</span>
                  </div>
                </>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Totals Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
        <Card>
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs md:text-sm font-medium">Total Revenue</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="text-lg md:text-xl lg:text-2xl font-bold font-serif">{formatCurrency(totalRevenue)}</div>
            <p className="text-xs text-muted-foreground">
              Retainer + Bonuses
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs md:text-sm font-medium">Total Owner Profit</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="text-lg md:text-xl lg:text-2xl font-bold font-serif text-positive">
              {formatCurrency(totalProfit)}
            </div>
            <p className="text-xs text-muted-foreground">
              Base + Bonus Share
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs md:text-sm font-medium">Total Operating Costs</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="text-lg md:text-xl lg:text-2xl font-bold font-serif">{formatCurrency(totalProjectCosts)}</div>
            <p className="text-xs text-muted-foreground">
              All project expenses
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Bonus Eligibility Snapshot */}
      {bonusEligibilityData.length > 0 && (
        <Card>
          <CardHeader className="px-3 sm:px-6">
            <CardTitle className="text-sm md:text-base font-medium">Bonus Eligibility Snapshot</CardTitle>
          </CardHeader>
          <CardContent className="px-0 md:px-4 lg:px-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-3 md:pl-0 text-xs md:text-sm">Person/Role</TableHead>
                    <TableHead className="hidden md:table-cell text-xs md:text-sm">Type</TableHead>
                    <TableHead className="text-xs md:text-sm">Bonuses</TableHead>
                    <TableHead className="text-right pr-3 md:pr-0 text-xs md:text-sm">Est. Bonus</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bonusEligibilityData.map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium pl-3 md:pl-0 text-xs md:text-sm">
                        <div>
                          <span>{entry.name}</span>
                          {entry.role && (
                            <span className="text-xs text-muted-foreground ml-1 hidden md:inline">
                              ({entry.role})
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground md:hidden">
                          {entry.type === 'specialist' ? 'Specialist' : 'Operations'}
                        </span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant={entry.type === 'specialist' ? 'default' : 'secondary'}>
                          {entry.type === 'specialist' ? 'Specialist' : 'Operations'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {entry.kickers.map((kicker, kidx) => (
                            <TierBadge key={kidx} tier={kicker.tier} showLabel={false} />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium text-positive pr-3 md:pr-0 text-xs md:text-sm">
                        {entry.totalBonus > 0 ? formatCurrency(entry.totalBonus) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Helper function to build bonus eligibility data
interface BonusEligibilityEntry {
  name: string;
  role?: string;
  type: 'specialist' | 'operations';
  kickers: Array<{ name: string; tier: string; amount: number }>;
  totalBonus: number;
}

function buildBonusEligibilityData(
  tacticalSpecialists: TacticalSpecialist[],
  operationsOwners: string[],
  performanceKickers: PerformanceKicker[],
  kickerCalculations: KickerCalculation[]
): BonusEligibilityEntry[] {
  const entries: BonusEligibilityEntry[] = [];

  // Add specialists
  tacticalSpecialists
    .filter(spec => spec.handlesKickers)
    .forEach(spec => {
      const kickers: Array<{ name: string; tier: string; amount: number }> = [];
      let totalBonus = 0;

      kickerCalculations.forEach(calc => {
        const kicker = performanceKickers.find(k => k.id === calc.id);
        if (kicker && kicker.assignedSpecialistIds?.includes(spec.id)) {
          kickers.push({
            name: calc.name || 'Unnamed',
            tier: kicker.selectedTier,
            amount: calc.perPersonPayout || 0
          });
          totalBonus += calc.perPersonPayout || 0;
        }
      });

      if (kickers.length > 0 || spec.handlesKickers) {
        entries.push({
          name: spec.name || 'Unnamed',
          role: spec.assignedOwner,
          type: 'specialist',
          kickers,
          totalBonus
        });
      }
    });

  // Add operations owners (group them)
  const opsOwnerKickers: Array<{ name: string; tier: string; amount: number }> = [];
  let opsOwnerTotalPerPerson = 0;

  kickerCalculations.forEach(calc => {
    const kicker = performanceKickers.find(k => k.id === calc.id);
    if (kicker && kicker.operationsOwnersEligible) {
      opsOwnerKickers.push({
        name: calc.name || 'Unnamed',
        tier: kicker.selectedTier,
        amount: calc.perPersonPayout || 0
      });
      opsOwnerTotalPerPerson += calc.perPersonPayout || 0;
    }
  });

  if (opsOwnerKickers.length > 0) {
    operationsOwners.forEach(owner => {
      entries.push({
        name: owner,
        type: 'operations',
        kickers: opsOwnerKickers,
        totalBonus: opsOwnerTotalPerPerson
      });
    });
  }

  return entries;
}
